import { orgLocalDate } from "@njbsoft/utils";

/** Filters out Go zero dates (0001-01-01) and falsy values. */
export function isValidDate(val: unknown): val is string {
  if (!val || typeof val !== "string") return false;
  return !val.startsWith("0001-01-01");
}

/**
 * Format an ISO 8601 timestamp (e.g. `2026-04-22T14:05:12Z`) as a localized
 * date + time string in the viewer's timezone. Falls back to the raw string
 * when the value is empty, a Go zero time, or otherwise unparseable.
 */
export function formatDateTime(val: string | null | undefined): string {
  if (!val) return "-";
  if (!isValidDate(val)) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString();
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const LONG_DATE: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

/**
 * A date-only string (`YYYY-MM-DD`) as the long-month day the portal prints
 * ("Sep 14, 2026" in `en`) in `locale`. The day is built from its parts in the
 * viewer's zone and formatted in that same zone, so it never shifts the way
 * `new Date("2026-09-14")` (UTC midnight) does west of UTC. Anything that is
 * not a real `YYYY-MM-DD` day is returned as given.
 */
export function formatLongDate(dateOnly: string, locale: string): string {
  const match = DATE_ONLY.exec(dateOnly);
  if (!match) return dateOnly;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const day = new Date(y, m - 1, d);
  if (day.getFullYear() !== y || day.getMonth() !== m - 1 || day.getDate() !== d) {
    return dateOnly;
  }
  return day.toLocaleDateString(locale, LONG_DATE);
}

/**
 * The calendar day an ISO 8601 instant fell on in the utility's IANA
 * `timezone`, printed long-month like `formatLongDate`: the day the utility's
 * own staff see on the record, wherever the reader sits. A Go zero time, or no
 * zone to read it in, prints the dash; an unparseable value prints as given.
 */
export function formatLongInstantDay(
  iso: string,
  timezone: string | undefined,
  locale: string,
): string {
  if (!isValidDate(iso) || !timezone) return "-";
  if (isNaN(new Date(iso).getTime())) return iso;
  return formatLongDate(orgLocalDate(iso, timezone), locale);
}
